import { oc } from "@orpc/contract";
import { z } from "zod";
import { OffsetPageInputSchema } from "../../schemas/common";
import { SpaQuoteType } from "@erp/shared-types";
import { StartFileExportResponseSchema } from "../core/file-exports.contract";

export const SpaLineItemResponseSchema = z.object({
  id: z.uuid(),
  spaId: z.uuid(),
  productNumber: z.string(),
  rowIndex: z.number().int(),
  rawData: z.record(z.string(), z.unknown()),
  keptFlag: z.boolean(),
  dropReason: z
    .enum([
      "blank_row",
      "missing_catalog",
      "deleted",
      "expired",
      "future_start",
      "ecom_only",
    ])
    .nullable(),
  currentMargin: z.number().nullable(),
  requestedMargin: z.number().nullable(),
  approvalDecision: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const SpaLinkedRequestResponseSchema = z.object({
  id: z.uuid(),
  requestNumber: z.string(),
  status: z.string(),
  createdAt: z.coerce.date(),
});

export const SpaAgreementCandidatesSchema = z.object({
  status: z.enum(["not_checked", "vendor_absent", "checked"]),
  vendorPrefix: z.string(),
  candidates: z.array(
    z.object({
      agreementNumber: z.string(),
      agreementType: z.string(),
      startDate: z.string().nullable(),
      endDate: z.string().nullable(),
    }),
  ),
});

export const SpaResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  spaRequestId: z.uuid().nullable(),
  spaSourceId: z.uuid().nullable(),
  sourceDocumentId: z.uuid(),
  emailReceivedAt: z.coerce.date().nullable(),
  vendor: z.string(),
  spaNumber: z.string(),
  revision: z.string().nullable(),
  quoteType: z.nativeEnum(SpaQuoteType).nullable(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  /**
   * How many distinct windows the line items describe, when startDate/endDate
   * were derived from them rather than stated in the document header.
   *
   * Greater than 1 means the dates are a SPAN across several windows, not one
   * effective window, so they must not be read as uniform coverage. Null where
   * the count does not apply: the supplier stated the window, or the record
   * predates the column.
   */
  lineWindowCount: z.number().int().nullable(),
  currency: z.string().nullable(),
  customerNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  division: z.string().nullable(),
  request: SpaLinkedRequestResponseSchema.nullable(),
  agreementCandidates: SpaAgreementCandidatesSchema,
  lineItems: z.array(SpaLineItemResponseSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const SpaListItemSchema = SpaResponseSchema.omit({
  lineItems: true,
  agreementCandidates: true,
});

export const ListSpasInputSchema = OffsetPageInputSchema.extend({
  search: z.string().optional(),
  vendor: z.string().optional(),
  /** Substring match against SpaLineItem.productNumber. */
  productNumber: z.string().optional(),
  /** Agreement validity window overlaps [validFrom, validTo] (both optional, either bound open-ended). */
  validFrom: z.string().optional(),
  validTo: z.string().optional(),
});

export const GetSpaInputSchema = z.object({
  id: z.uuid(),
});

export const DeleteSpaInputSchema = z.object({
  id: z.uuid(),
});

export const ExportRockwell845ExcelInputSchema = z.object({
  id: z.uuid(),
});

export const spasContracts = {
  list: oc
    .input(ListSpasInputSchema)
    .output(
      z.object({
        items: z.array(SpaListItemSchema),
        total: z.number().int(),
      }),
    )
    .route({
      method: "GET",
      summary: "List SPAs",
      description:
        "Paginated special pricing agreements, searchable by SPA number, vendor, customer or division, and filterable by product number and validity date range.",
    }),

  get: oc.input(GetSpaInputSchema).output(SpaResponseSchema).route({
    method: "GET",
    summary: "Get a SPA",
    description:
      "Returns one special pricing agreement with its line items, margins, linked request, and matching ERP price agreement candidates.",
  }),

  /**
   * Soft-delete a persisted SPA record and its line items. The source request remains.
   */
  delete: oc
    .input(DeleteSpaInputSchema)
    .output(
      z.object({
        id: z.uuid(),
        spaRequestId: z.uuid().nullable(),
        deleted: z.literal(true),
      }),
    )
    .route({
      method: "DELETE",
      summary: "Delete a SPA",
      description:
        "Soft-deletes a special pricing agreement record and its line items; the originating SPA request is kept.",
    }),

  /**
   * The pricing team's Rockwell 845 sheet (their Alteryx batch macro, ported),
   * rebuilt from the SPA's raw source file by a file-export job. Rockwell SPAs
   * only. Returns the job; the file is collected through core.fileExports.
   */
  exportRockwell845Excel: oc
    .input(ExportRockwell845ExcelInputSchema)
    .output(StartFileExportResponseSchema)
    .route({
      method: "POST",
      summary: "Export a Rockwell 845 as Excel",
      description:
        "Starts a background export of a Rockwell SPA's source 845 as the pricing team's Excel sheet (one row per distributor location and priced line). Returns the job id; download the file through the file-exports endpoint once the job succeeds.",
    }),
} as const;

export type SpaResponse = z.infer<typeof SpaResponseSchema>;
export type SpaListItem = z.infer<typeof SpaListItemSchema>;
export type SpaLineItemResponse = z.infer<typeof SpaLineItemResponseSchema>;
